import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosPersonalAPI, bomberosCompaniaAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    Users,
    Plus,
    Search,
    ArrowLeft,
    Filter,
    Eye,
    Edit,
    UserCheck,
    UserX,
    Trash2,
    Download,
    Power,
    PowerOff,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function BomberosPersonalPage() {
    const navigate = useNavigate();
    const { tienePermiso } = useBomberosPermisos();
    const [loading, setLoading] = useState(true);
    const [bomberos, setBomberos] = useState([]);
    const [companias, setCompanias] = useState([]);
    const [filteredBomberos, setFilteredBomberos] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCompania, setFilterCompania] = useState('all');
    const [filterGrado, setFilterGrado] = useState('all');
    const [filterActivo, setFilterActivo] = useState('all');

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        filterBomberos();
    }, [bomberos, searchTerm, filterCompania, filterGrado, filterActivo]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [bomberosRes, companiasRes] = await Promise.all([
                bomberosPersonalAPI.getAll(),
                bomberosCompaniaAPI.getAll(),
            ]);
            setBomberos(bomberosRes.data);
            setCompanias(companiasRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const filterBomberos = () => {
        let filtered = [...bomberos];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(b =>
                b.nombre_completo?.toLowerCase().includes(term) ||
                b.rut?.toLowerCase().includes(term) ||
                b.email?.toLowerCase().includes(term)
            );
        }

        if (filterCompania !== 'all') {
            filtered = filtered.filter(b => b.compania_id === filterCompania);
        }

        if (filterGrado !== 'all') {
            filtered = filtered.filter(b => b.grado === filterGrado);
        }

        if (filterActivo !== 'all') {
            filtered = filtered.filter(b => b.activo === (filterActivo === 'true'));
        }

        setFilteredBomberos(filtered);
    };

    const handleDesactivar = async (bombero) => {
        if (!window.confirm(`¿Desactivar a ${bombero.nombre_completo}? Quedará en la lista como inactivo.`)) return;
        try {
            await bomberosPersonalAPI.delete(bombero.id, false);
            toast.success('Bombero desactivado');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al desactivar');
        }
    };

    const handleActivar = async (bombero) => {
        try {
            await bomberosPersonalAPI.activar(bombero.id);
            toast.success('Bombero reactivado');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al activar');
        }
    };

    const handleEliminarPermanente = async (bombero) => {
        const confirmText = `ELIMINAR PERMANENTEMENTE a ${bombero.nombre_completo}.\n\nEsta acción NO se puede deshacer. ¿Continuar?`;
        if (!window.confirm(confirmText)) return;
        if (!window.confirm('¿Está COMPLETAMENTE seguro? Se borrarán todos los datos de este bombero.')) return;
        try {
            await bomberosPersonalAPI.delete(bombero.id, true);
            toast.success('Bombero eliminado permanentemente');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar');
        }
    };

    const handleExportXLSX = () => {
        const data = filteredBomberos.map(b => ({
            'RUT': b.rut,
            'Nombres': b.nombres,
            'Apellido Paterno': b.apellido_paterno,
            'Apellido Materno': b.apellido_materno || '',
            'Grado': b.grado,
            'Cargo': b.cargo || '',
            'Compañía': b.compania_nombre || '',
            'Email': b.email || '',
            'Teléfono': b.telefono || '',
            'Dirección': b.direccion || '',
            'Grupo Sanguíneo': b.grupo_sanguineo || '',
            'Fecha Ingreso': b.fecha_ingreso || '',
            'Fecha Nacimiento': b.fecha_nacimiento || '',
            'Contacto Emergencia': b.contacto_emergencia || '',
            'Tel. Emergencia': b.telefono_emergencia || '',
            'Estado': b.activo ? 'Activo' : 'Inactivo',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 18 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Personal Bomberos');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf]), `bomberos_personal_${new Date().toISOString().slice(0,10)}.xlsx`);
        toast.success(`Exportados ${data.length} registros`);
    };

    const getGradoBadgeColor = (grado) => {
        switch (grado) {
            case 'comandante': return 'bg-red-500 text-white';
            case 'capitan': return 'bg-orange-500 text-white';
            case 'teniente': return 'bg-yellow-500 text-black';
            case 'bombero': return 'bg-blue-500 text-white';
            case 'voluntario': return 'bg-green-500 text-white';
            default: return 'bg-gray-500 text-white';
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Users className="h-6 w-6" />
                            Personal de Bomberos
                        </h1>
                        <p className="text-muted-foreground">{filteredBomberos.length} bomberos encontrados</p>
                    </div>
                </div>
                {tienePermiso('crear_personal') && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={handleExportXLSX} data-testid="export-xlsx-btn">
                            <Download className="h-4 w-4 mr-2" />
                            XLSX
                        </Button>
                        <Button size="sm" onClick={() => navigate('/bomberos/personal/nuevo')} data-testid="nuevo-bombero-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Nuevo Bombero
                        </Button>
                    </div>
                )}
            </div>

            {/* Filters */}
            <Card className="border-2">
                <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por nombre, RUT o email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={filterCompania} onValueChange={setFilterCompania}>
                            <SelectTrigger className="w-full md:w-48" data-testid="filter-compania">
                                <SelectValue placeholder="Compañía" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas las Compañías</SelectItem>
                                {companias.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterGrado} onValueChange={setFilterGrado}>
                            <SelectTrigger className="w-full md:w-40" data-testid="filter-grado">
                                <SelectValue placeholder="Grado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los Grados</SelectItem>
                                <SelectItem value="comandante">Comandante</SelectItem>
                                <SelectItem value="capitan">Capitán</SelectItem>
                                <SelectItem value="teniente">Teniente</SelectItem>
                                <SelectItem value="bombero">Bombero</SelectItem>
                                <SelectItem value="voluntario">Voluntario</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterActivo} onValueChange={setFilterActivo}>
                            <SelectTrigger className="w-full md:w-32" data-testid="filter-activo">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="true">Activos</SelectItem>
                                <SelectItem value="false">Inactivos</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="border-2">
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>RUT</TableHead>
                                <TableHead>Nombre</TableHead>
                                <TableHead>Grado</TableHead>
                                <TableHead>Compañía</TableHead>
                                <TableHead>Malla</TableHead>
                                <TableHead>Contacto</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredBomberos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                        No se encontraron bomberos con los filtros aplicados
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredBomberos.map((bombero) => (
                                    <TableRow key={bombero.id} data-testid={`row-${bombero.rut}`}>
                                        <TableCell className="font-mono text-sm">{bombero.rut}</TableCell>
                                        <TableCell>
                                            <div>
                                                <p className="font-medium">{bombero.nombre_completo}</p>
                                                {bombero.cargo && (
                                                    <p className="text-xs text-muted-foreground">{bombero.cargo}</p>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={`capitalize ${getGradoBadgeColor(bombero.grado)}`}>
                                                {bombero.grado}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm">{bombero.compania_nombre || '-'}</TableCell>
                                        <TableCell>
                                            {bombero.malla_asignada ? (
                                                <Badge variant="outline" className="text-xs border-amber-500 text-amber-700">
                                                    {bombero.malla_asignada}
                                                </Badge>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm">
                                                {bombero.email && <p className="truncate max-w-32">{bombero.email}</p>}
                                                {bombero.telefono && <p className="text-muted-foreground">{bombero.telefono}</p>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {bombero.activo ? (
                                                <Badge variant="outline" className="text-green-600 border-green-600">
                                                    <UserCheck className="h-3 w-3 mr-1" />
                                                    Activo
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-red-600 border-red-600">
                                                    <UserX className="h-3 w-3 mr-1" />
                                                    Inactivo
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                {tienePermiso('ver_personal') && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/bomberos/personal/${bombero.id}`)}
                                                        data-testid={`ver-${bombero.rut}`}
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                {tienePermiso('editar_personal') && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/bomberos/personal/${bombero.id}/editar`)}
                                                        data-testid={`editar-${bombero.rut}`}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                {tienePermiso('eliminar_personal') && (
                                                    <>
                                                        {bombero.activo ? (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-yellow-500 hover:text-yellow-600"
                                                                onClick={() => handleDesactivar(bombero)}
                                                                title="Desactivar"
                                                                data-testid={`desactivar-${bombero.rut}`}
                                                            >
                                                                <PowerOff className="h-4 w-4" />
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-green-500 hover:text-green-600"
                                                                onClick={() => handleActivar(bombero)}
                                                                title="Reactivar"
                                                                data-testid={`activar-${bombero.rut}`}
                                                            >
                                                                <Power className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-destructive hover:text-destructive"
                                                            onClick={() => handleEliminarPermanente(bombero)}
                                                            title="Eliminar permanentemente"
                                                            data-testid={`eliminar-${bombero.rut}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {['comandante', 'capitan', 'teniente', 'bombero', 'voluntario'].map(grado => {
                    const count = bomberos.filter(b => b.grado === grado && b.activo).length;
                    return (
                        <Card key={grado} className="border">
                            <CardContent className="pt-4 pb-4 text-center">
                                <p className="text-2xl font-bold">{count}</p>
                                <p className="text-xs text-muted-foreground capitalize">{grado}s Activos</p>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
        </>
    );
}
